import { useState, useEffect, useRef } from 'react';
import WMIcon from '../ui/WMIcon';
import SignatureCanvas from '../ui/SignatureCanvas';
import PhotoUpload from '../ui/PhotoUpload';
import { useWorkOrders } from '../../context/WorkOrderContext';
import TimeTracker from '../ui/TimeTracker';

const STATUS_OPTIONS = [
  { value: 'pending',     label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done',        label: 'Done' },
  { value: 'on_hold',     label: 'On Hold' },
  { value: 'cancelled',   label: 'Cancelled' },
];

const STATUS_COLORS = {
  pending:     'text-amber-600 dark:text-amber-400',
  in_progress: 'text-blue-600 dark:text-blue-400',
  done:        'text-green-600 dark:text-green-400',
  on_hold:     'text-gray-500 dark:text-gray-400',
  cancelled:   'text-red-600 dark:text-red-400',
};

const TECH_COLORS = ['#2E8BF0', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#EC4899'];
function techColor(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return TECH_COLORS[Math.abs(hash) % TECH_COLORS.length];
}
function initials(name) {
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

export default function WorkOrderDetailPanel({ workOrder, onClose }) {
  const { updateWorkOrder, completeStep, setWorkOrderStatus } = useWorkOrders();

  const [notes, setNotes] = useState(workOrder.notes || '');
  const [newStepTitle, setNewStepTitle] = useState('');
  const [newTechName, setNewTechName] = useState('');
  const [showSignature, setShowSignature] = useState(false);
  const [stepNoteEditing, setStepNoteEditing] = useState(null); // stepId
  const [stepNoteValue, setStepNoteValue] = useState('');
  const panelRef = useRef(null);

  // Sync notes when workOrder changes from outside
  /* eslint-disable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */
  useEffect(() => { setNotes(workOrder.notes || ''); }, [workOrder.id]);
  /* eslint-enable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const wo = workOrder;
  const doneCount = wo.steps.filter(s => s.done).length;
  const totalCount = wo.steps.length;
  const pct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  function handleStatusChange(e) {
    setWorkOrderStatus(wo.id, e.target.value);
  }

  function handleNotesBlur() {
    updateWorkOrder(wo.id, { notes });
  }

  function handleAddStep() {
    if (!newStepTitle.trim()) return;
    const newStep = {
      id: `s-${Date.now()}`,
      title: newStepTitle.trim(),
      done: false,
      completedAt: null,
      note: '',
    };
    updateWorkOrder(wo.id, { steps: [...wo.steps, newStep] });
    setNewStepTitle('');
  }

  function handleAddTech() {
    const name = newTechName.trim();
    if (!name || wo.assignedTo.includes(name)) return;
    updateWorkOrder(wo.id, { assignedTo: [...wo.assignedTo, name] });
    setNewTechName('');
  }

  function handleRemoveTech(tech) {
    updateWorkOrder(wo.id, { assignedTo: wo.assignedTo.filter(t => t !== tech) });
  }

  function handleSaveSignature(sigData) {
    updateWorkOrder(wo.id, { signature: sigData });
    setWorkOrderStatus(wo.id, 'done');
    setShowSignature(false);
  }

  function handleSaveStepNote(stepId) {
    const steps = wo.steps.map(s =>
      s.id === stepId ? { ...s, note: stepNoteValue } : s
    );
    updateWorkOrder(wo.id, { steps });
    setStepNoteEditing(null);
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/20 dark:bg-black/40"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-[480px] bg-white dark:bg-[#1B2A3E] border-l border-gray-200 dark:border-[#243348] flex flex-col shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 dark:border-[#243348] flex-shrink-0">
          <div className="min-w-0 flex-1 pr-3">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-[var(--wm-text-ui)] font-mono font-semibold text-[#2E8BF0] uppercase tracking-wider">
                WO-{wo.id.replace('wo-', '').toUpperCase()}
              </span>
              {wo.estimateId && (
                <span className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE]">
                  Est: {wo.estimateId}
                </span>
              )}
            </div>
            <h2 className="text-[var(--wm-text-body)] font-semibold text-[#0F1923] dark:text-[#F8FAFE] truncate">
              {wo.vehicleLabel}
            </h2>
            <p className="text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">{wo.customerName}</p>
            {wo.customerPhone && (
              <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">{wo.customerPhone}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Status selector */}
            <select
              value={wo.status}
              onChange={handleStatusChange}
              className={`text-[var(--wm-text-ui)] font-medium border border-gray-200 dark:border-[#243348] rounded-lg px-2 py-1.5 bg-white dark:bg-[#243348] focus:outline-none focus:ring-2 focus:ring-[#2E8BF0] ${STATUS_COLORS[wo.status] || ''}`}
            >
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#243348] text-[#64748B] dark:text-[#7D93AE] transition-colors"
            >
              <WMIcon name="x-mark" className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">

          {/* Steps progress */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[var(--wm-text-ui)] font-semibold text-[#0F1923] dark:text-[#F8FAFE] uppercase tracking-wide">
                Steps
              </h3>
              <span className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">{doneCount}/{totalCount} done</span>
            </div>

            {/* Progress bar */}
            <div className="h-1.5 rounded-full bg-gray-100 dark:bg-[#243348] overflow-hidden mb-3">
              <div
                className="h-full rounded-full bg-[#2E8BF0] transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>

            {/* Step list */}
            <div className="space-y-1">
              {wo.steps.map(step => (
                <div key={step.id} className="rounded-lg border border-gray-100 dark:border-[#243348] overflow-hidden">
                  <div className="flex items-center gap-2.5 px-3 py-2.5">
                    <button
                      onClick={() => completeStep(wo.id, step.id)}
                      className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                        step.done
                          ? 'bg-[#2E8BF0] border-[#2E8BF0]'
                          : 'border-gray-300 dark:border-[#3D5068] hover:border-[#2E8BF0]'
                      }`}
                    >
                      {step.done && (
                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                      )}
                    </button>
                    <span className={`flex-1 text-[var(--wm-text-body)] ${step.done ? 'line-through text-[#64748B] dark:text-[#7D93AE]' : 'text-[#0F1923] dark:text-[#F8FAFE]'}`}>
                      {step.title}
                    </span>
                    {step.completedAt && (
                      <span className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE]">
                        {new Date(step.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    )}
                    <TimeTracker
                      workOrderId={wo.id}
                      stepId={step.id}
                      timeEntries={wo.timeEntries || []}
                      onStart={(entry) => updateWorkOrder(wo.id, {
                        timeEntries: [...(wo.timeEntries || []), entry],
                      })}
                      onStop={(entry) => updateWorkOrder(wo.id, {
                        timeEntries: (wo.timeEntries || []).map(e =>
                          e.stepId === entry.stepId && !e.stoppedAt ? { ...e, ...entry } : e
                        ),
                      })}
                    />
                    <button
                      onClick={() => {
                        setStepNoteEditing(stepNoteEditing === step.id ? null : step.id);
                        setStepNoteValue(step.note || '');
                      }}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-[#243348] text-[#64748B] dark:text-[#7D93AE] transition-colors"
                      title="Add note"
                    >
                      <WMIcon name="chat-bubble" className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {stepNoteEditing === step.id && (
                    <div className="px-3 pb-2.5 pt-0 border-t border-gray-100 dark:border-[#243348]">
                      <textarea
                        value={stepNoteValue}
                        onChange={e => setStepNoteValue(e.target.value)}
                        placeholder="Step note…"
                        rows={2}
                        className="w-full text-[var(--wm-text-ui)] border border-gray-200 dark:border-[#3D5068] rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#2E8BF0] bg-white dark:bg-[#243348] text-[#0F1923] dark:text-[#F8FAFE] resize-none"
                      />
                      <div className="flex gap-2 mt-1.5">
                        <button
                          onClick={() => handleSaveStepNote(step.id)}
                          className="text-[var(--wm-text-caption)] px-2.5 py-1 bg-[#2E8BF0] text-white rounded font-medium hover:bg-blue-600 transition-colors"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setStepNoteEditing(null)}
                          className="text-[var(--wm-text-caption)] px-2.5 py-1 border border-gray-200 dark:border-[#243348] text-[#64748B] dark:text-[#7D93AE] rounded hover:bg-gray-50 dark:hover:bg-[#243348] transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                      {step.note && (
                        <p className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE] mt-1 italic">{step.note}</p>
                      )}
                    </div>
                  )}
                  {step.note && stepNoteEditing !== step.id && (
                    <div className="px-3 pb-2 text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE] italic border-t border-gray-100 dark:border-[#243348] pt-1.5">
                      {step.note}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add step */}
            <div className="flex gap-2 mt-2">
              <input
                type="text"
                value={newStepTitle}
                onChange={e => setNewStepTitle(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddStep()}
                placeholder="Add a step…"
                className="flex-1 text-[var(--wm-text-ui)] border border-gray-200 dark:border-[#3D5068] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2E8BF0] bg-white dark:bg-[#243348] text-[#0F1923] dark:text-[#F8FAFE]"
              />
              <button
                onClick={handleAddStep}
                disabled={!newStepTitle.trim()}
                className="px-3 py-2 rounded-lg bg-[#2E8BF0] text-white text-[var(--wm-text-ui)] font-medium hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Add
              </button>
            </div>
          </section>

          {/* Assigned techs */}
          <section>
            <h3 className="text-[var(--wm-text-ui)] font-semibold text-[#0F1923] dark:text-[#F8FAFE] uppercase tracking-wide mb-2">
              Assigned Techs
            </h3>
            <div className="flex flex-wrap gap-2 mb-2">
              {wo.assignedTo.map(tech => {
                const color = techColor(tech);
                return (
                  <span
                    key={tech}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[var(--wm-text-ui)] font-medium border"
                    style={{ backgroundColor: `${color}18`, borderColor: `${color}40`, color }}
                  >
                    {initials(tech)} {tech}
                    <button
                      onClick={() => handleRemoveTech(tech)}
                      className="ml-0.5 opacity-60 hover:opacity-100 transition-opacity"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                );
              })}
              {wo.assignedTo.length === 0 && (
                <span className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] italic">No techs assigned</span>
              )}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTechName}
                onChange={e => setNewTechName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddTech()}
                placeholder="Tech name…"
                className="flex-1 text-[var(--wm-text-ui)] border border-gray-200 dark:border-[#3D5068] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2E8BF0] bg-white dark:bg-[#243348] text-[#0F1923] dark:text-[#F8FAFE]"
              />
              <button
                onClick={handleAddTech}
                disabled={!newTechName.trim()}
                className="px-3 py-2 rounded-lg bg-[#2E8BF0] text-white text-[var(--wm-text-ui)] font-medium hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Add
              </button>
            </div>
          </section>

          {/* Notes */}
          <section>
            <h3 className="text-[var(--wm-text-ui)] font-semibold text-[#0F1923] dark:text-[#F8FAFE] uppercase tracking-wide mb-2">
              Notes
            </h3>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              onBlur={handleNotesBlur}
              rows={3}
              placeholder="Work order notes…"
              className="w-full text-[var(--wm-text-body)] border border-gray-200 dark:border-[#3D5068] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2E8BF0] bg-white dark:bg-[#243348] text-[#0F1923] dark:text-[#F8FAFE] resize-none"
            />
          </section>

          {/* Photos */}
          <section>
            <h3 className="text-[var(--wm-text-ui)] font-semibold text-[#0F1923] dark:text-[#F8FAFE] uppercase tracking-wide mb-2">
              Photos
            </h3>
            <PhotoUpload
              photos={workOrder.photos || []}
              onAdd={(photo) => updateWorkOrder(workOrder.id, { photos: [...(workOrder.photos || []), photo] })}
              onRemove={(url) => updateWorkOrder(workOrder.id, { photos: (workOrder.photos || []).filter(p => p.url !== url) })}
              entityId={workOrder.id}
              entityType="work_order"
            />
          </section>

          {/* Comms log placeholder */}
          <section>
            <h3 className="text-[var(--wm-text-ui)] font-semibold text-[#0F1923] dark:text-[#F8FAFE] uppercase tracking-wide mb-2">
              Communications
            </h3>
            <div className="rounded-xl border border-dashed border-gray-200 dark:border-[#243348] p-4 text-center">
              <WMIcon name="chat-bubble" className="w-6 h-6 text-[#64748B] dark:text-[#7D93AE] mx-auto mb-1" />
              <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">Comms log — Phase 12</p>
            </div>
          </section>

          {/* Signature */}
          <section>
            <h3 className="text-[var(--wm-text-ui)] font-semibold text-[#0F1923] dark:text-[#F8FAFE] uppercase tracking-wide mb-2">
              Signature
            </h3>
            {wo.signature && !showSignature ? (
              <SignatureCanvas
                existingSignature={wo.signature}
                readOnly
              />
            ) : showSignature ? (
              <SignatureCanvas
                signerNameLabel="Customer Full Name"
                onSave={handleSaveSignature}
                onCancel={() => setShowSignature(false)}
              />
            ) : (
              <button
                onClick={() => setShowSignature(true)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-[#2E8BF0] text-[#2E8BF0] text-[var(--wm-text-body)] font-medium hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors"
              >
                <WMIcon name="pencil" className="w-4 h-4" />
                Complete &amp; Sign
              </button>
            )}
          </section>

          {/* Scheduled date */}
          {wo.scheduledStart && (
            <section className="pb-2">
              <h3 className="text-[var(--wm-text-ui)] font-semibold text-[#0F1923] dark:text-[#F8FAFE] uppercase tracking-wide mb-1">
                Scheduled
              </h3>
              <p className="text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE]">
                {new Date(wo.scheduledStart).toLocaleString('en-US', {
                  weekday: 'short', month: 'short', day: 'numeric',
                  hour: 'numeric', minute: '2-digit',
                })}
              </p>
            </section>
          )}

        </div>
      </div>
    </>
  );
}
